import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";

import { CaptchaRequiredError, fetchOrder } from "~/api";
import { FetchOrderVariables } from "~/common/types/api";

import { OrderDetailsResponseRaw } from "./useOrderDetails.types";
import {
  getOrderItemsFromOrderDetailsResponse,
  transformOrderDetailsResponse,
} from "./useOrderDetails.util";

export const useOrderDetails = (
  {
    orderId,
    customerEmail,
    includeRegisteredItems,
    altcha,
  }: FetchOrderVariables,
  onSuccess?: () => void,
  enabled = true
) => {
  const {
    data: response,
    error,
    isLoading,
    isFetching,
    isError,
    isFetchedAfterMount,
    isPreviousData,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<OrderDetailsResponseRaw, Error>(
    [orderId, customerEmail, includeRegisteredItems, altcha],
    ({ pageParam = 1 }) =>
      fetchOrder({
        orderId,
        customerEmail,
        currentPage: pageParam,
        includeRegisteredItems,
        altcha,
      }),
    {
      enabled,
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages = []) => {
        if (lastPage?.order?.[0]?.more) {
          return allPages.length + 1;
        }
      },
      onSuccess,
    }
  );
  const data = useMemo(
    () => transformOrderDetailsResponse(response?.pages?.[0]),
    [response]
  );
  const orderItems = useMemo(
    () => getOrderItemsFromOrderDetailsResponse(response?.pages),
    [response]
  );
  return {
    isError,
    isFetchedAfterMount,
    isPreviousData,
    isLoading: isLoading || isFetching,
    errorMessage: error?.message ?? "",
    // Set only when the captcha tier challenged this lookup; the solved
    // payload re-enters through the altcha query key.
    challengeUrl:
      error instanceof CaptchaRequiredError ? error.challengeUrl : "",
    data,
    orderItems,
    hasMoreOrderItems: hasNextPage,
    loadMoreOrderItems: fetchNextPage,
  };
};
